import { useQueryClient } from "@tanstack/react-query";
import type { PaginationState, RowSelectionState, SortingState } from "@tanstack/react-table";
import { produce } from "immer";
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounceValue } from "usehooks-ts";
import { z } from "zod";

import { DeleteIcon } from "@/assets/icons/general/Delete";
import { Edit2Icon } from "@/assets/icons/general/Edit2";
import { MarkAsReadIcon } from "@/assets/icons/general/MarkAsRead";
import { PlusIcon } from "@/assets/icons/general/Plus";
import { SearchIcon } from "@/assets/icons/general/Search";
import { Button } from "@/components/ui/buttons/Button/Button";
import { IconButton } from "@/components/ui/buttons/IconButton/IconButton";
import { TextButton } from "@/components/ui/buttons/TextButton/TextButton";
import { TextInput } from "@/components/ui/inputs/TextInput/TextInput";
import { ActionModal } from "@/components/ui/overlays/Modal/ActionModal";
import { useToast } from "@/components/ui/status/Toast/useToast";
import { Table } from "@/components/ui/table/Table";
import { Typography } from "@/components/ui/text/Typography/Typography";
import UploadsPageDeleteProfilesModal from "@/components/uploads/modal/UploadsPageDeleteProfilesModal";
import UploadsPageEditProfilesModal from "@/components/uploads/modal/UploadsPageEditProfilesModal";
import UploadsPageFilesUploadModal from "@/components/uploads/modal/UploadsPageFilesUploadModal";
import { NewPagination } from "@/components/uploads/NewPagination";
import UploadsPageDropZone from "@/components/uploads/UploadsPageDropZone";
import { UploadsPagePillFilters } from "@/components/uploads/UploadsPagePillFilters";
import useFileValidationCheck from "@/components/uploads/useFileValidationCheck";
import useUploadsPageColumns from "@/components/uploads/useUploadsPageColumns";
import { BizProfileModels } from "@/data/bizProfile/bizProfile.models";
import { BizProfileQueries } from "@/data/bizProfile/bizProfile.queries";
import { ProfileUploadsContext } from "@/data/uploads/uploads.context";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { logger } from "@/util/logger";
import { TableUtils } from "@/util/table.utils";

export const experienceFilter = [
  { id: "0", min: undefined, max: 1 }, // <1y
  { id: "1", min: 1, max: 5 }, // 1-5y
  { id: "2", min: 5, max: 10 }, // 5-10y
  { id: "3", min: 10, max: 15 }, // 10-15y
  { id: "4", min: 16, max: 80 }, // >15y
];

// Zod schema for localStorage state
const uploadsPageStateSchema = z.object({
  selectedTags: z.record(z.string(), z.array(z.string())).optional(),
  pagination: z.object({
    pageIndex: z.number().min(0),
    pageSize: z.number().min(1).max(100),
  }),
  sorting: z
    .array(
      z.object({
        id: z.string(),
        desc: z.boolean(),
      }),
    )
    .optional(),
  searchValue: z.string().optional(),
});

export const UploadsPageTableState = {
  key: "uploads-filters",
  schema: uploadsPageStateSchema,
};

export const getFiltersObject = (filters: {
  limit: number;
  order?: string;
  page?: number;
  cursor?: string;
  filterExperienceYearsMax?: number;
  filterExperienceYearsMin?: number;
  filterRating?: BizProfileModels.BizProfileListFilterRatingParam;
  filterMsaLocation?: string;
  filterTagName?: BizProfileModels.BizProfileListFilterTagNameParam;
  filterJobTitle?: string;
  filterSearch?: string;
}) => {
  return {
    limit: filters.limit,
    order: filters.order,
    page: filters.page,
    cursor: filters.cursor,
    filterExperienceYearsMax: filters.filterExperienceYearsMax,
    filterExperienceYearsMin: filters.filterExperienceYearsMin,
    filterRating: filters.filterRating,
    filterMsaLocation: filters.filterMsaLocation,
    filterTagName: filters.filterTagName,
    filterJobTitle: filters.filterJobTitle,
    filterSearch: filters.filterSearch,
  };
};

export const getProfilesListListQueryKey = (filters: {
  limit: number;
  order?: string;
  page?: number;
  cursor?: string;
  filterExperienceYearsMax?: number;
  filterExperienceYearsMin?: number;
  filterRating?: BizProfileModels.BizProfileListFilterRatingParam;
  filterMsaLocation?: string;
  filterTagName?: BizProfileModels.BizProfileListFilterTagNameParam;
  filterJobTitle?: string;
  filterSearch?: string;
}) => {
  return [
    ...BizProfileQueries.keys.list(
      filters.limit,
      filters.order,
      filters.page,
      undefined,
      filters.filterExperienceYearsMax,
      filters.filterExperienceYearsMin,
      filters.filterRating,
      filters.filterMsaLocation,
      filters.filterTagName,
      filters.filterJobTitle,
      filters.filterSearch,
    ),
  ];
};

const UploadsPage = () => {
  const [totalPages, setTotalPages] = useState(0);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [selectAllResumes, setSelectAllResumes] = useState(false);
  const { value: tableStateLocalStorage, setValue: updateTableStateLocalStorage2 } =
    useLocalStorage(UploadsPageTableState);

  const [selectedTags, setSelectedTags] = useState<Map<string, string[]>>(new Map());
  const [sorting, setSorting] = useState<SortingState>([]);
  const [search, setSearch] = useState(tableStateLocalStorage?.searchValue || "");
  const [debouncedSearchTerm] = useDebounceValue(search, 500);

  const [isProfileListRefetchNeeded, setIsProfileListRefetchNeeded] = useState(false);
  const [isDeleteProfileModalOpen, setIsDeleteProfileModalOpen] = useState<
    BizProfileModels.BizProfileListDto | undefined
  >(undefined);
  const [firstLoad, setFirstLoad] = useState(true);

  useEffect(() => {
    if (tableStateLocalStorage && firstLoad) {
      setPagination(tableStateLocalStorage.pagination);
      setSelectedTags(new Map(Object.entries(tableStateLocalStorage.selectedTags || {})));
      setSearch(tableStateLocalStorage.searchValue || "");
      setSorting(tableStateLocalStorage.sorting || []);
      setFirstLoad(false);
    }
  }, [tableStateLocalStorage, firstLoad]);

  const filters = getFiltersObject({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    filterExperienceYearsMax:
      selectedTags.get("experience")?.map((exp) => experienceFilter.find((e) => e.id === exp))?.[0]?.max || undefined,
    filterExperienceYearsMin:
      selectedTags.get("experience")?.map((exp) => experienceFilter.find((e) => e.id === exp))?.[0]?.min || undefined,
    filterRating:
      (selectedTags.get("rating") as unknown as number[])?.map((rating) =>
        rating.toString() === "Unrated" ? -1 : rating,
      ) || [],
    filterMsaLocation: selectedTags.get("location")?.[0] || "",
    filterTagName: selectedTags.get("tag") || [],
    filterJobTitle: selectedTags.get("jobTitle")?.[0] || "",
    order: ["readBy", sorting?.[0] ? `${sorting[0].desc ? "-" : "+"}${sorting[0].id}` : "+rank"].join(","),
    filterSearch: debouncedSearchTerm,
  });

  const {
    data: profiles,
    isLoading: isLoadingProfiles,
    refetch: refetchProfiles,
  } = BizProfileQueries.useList(filters, {
    staleTime: 0,
    refetchInterval: isProfileListRefetchNeeded ? 10 * 1000 : false,
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditUploadsModalOpen, setIsEditUploadsModalOpen] = useState(false);
  const [singleProfileEdit, setSingleProfileEdit] = useState<BizProfileModels.BizProfileListDto>();

  const { isModalOpen, setIsModalOpen, setSelectedFiles, selectedFiles } = ProfileUploadsContext();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const { t } = useTranslation();
  const { successToast, errorToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { mutate: deleteBizProfile } = BizProfileQueries.useDeleteBizProfileById({
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({
        queryKey: getProfilesListListQueryKey(filters),
      });

      const previousData = queryClient.getQueryData(getProfilesListListQueryKey(filters));

      queryClient.setQueryData(getProfilesListListQueryKey(filters), (old: any) => {
        return produce(old, (draft: { items?: any[]; totalItems?: number }) => {
          if (draft?.items) {
            const newItems = draft.items.filter((item) => item.id !== id);
            const newTotalItems = (draft.totalItems || 0) - 1;
            Object.assign(draft, { items: newItems, totalItems: newTotalItems });
          }
        });
      });

      setIsDeleteProfileModalOpen(undefined);
      successToast({
        text: t("uploads.userUploads.delete-success"),
      });
      return { previousData } as { previousData: unknown };
    },
    onError: (err, _variables, context: unknown) => {
      const typedContext = context as { previousData: unknown } | undefined;
      if (typedContext?.previousData) {
        queryClient.setQueryData([getProfilesListListQueryKey(filters)], typedContext.previousData);
      }
      logger.error("Error deleting biz profile", { error: err });
      errorToast({
        text: `${t("uploads.userUploads.delete-error")} - ${err.message}`,
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [...BizProfileQueries.keys.all] });
    },
  });
  const { mutate: markAsRead } = BizProfileQueries.useMarkAllAsRead({
    onMutate: async ({ data: { ids } }) => {
      await queryClient.cancelQueries({
        queryKey: [...BizProfileQueries.keys.all],
      });

      const previousData = queryClient.getQueryData([...BizProfileQueries.keys.all]);

      queryClient.setQueriesData({ queryKey: [...BizProfileQueries.keys.all] }, (old: any) => {
        if (!old?.items) return old;

        return produce(old, (draft: any) => {
          draft.items.forEach((item: any) => {
            if (ids.includes(item.id)) {
              // oxlint-disable-next-line no-param-reassign
              item.isRead = true;
            }
          });
        });
      });
      successToast({
        text: t("uploads.userUploads.mark-as-read-multiple-success"),
      });
      return { previousData };
    },
    onError: (err, _variables, context: unknown) => {
      const typedContext = context as { previousData: unknown } | undefined;
      if (typedContext?.previousData) {
        queryClient.setQueriesData({ queryKey: [...BizProfileQueries.keys.all] }, typedContext.previousData);
      }
      logger.error("Failed to mark profile as read", { error: err });
    },
  });

  useEffect(() => {
    if (profiles?.totalItems) {
      setTotalPages(Math.ceil((profiles?.totalItems || 0) / pagination.pageSize));
    }
  }, [profiles?.totalItems, pagination.pageSize]);

  useEffect(() => {
    // reset to first page if total pages is less than current page saved in local storage
    if (totalPages < pagination.pageIndex + 1 && !firstLoad) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      updateTableStateLocalStorage2((curr) => {
        return {
          ...curr,
          pagination: {
            pageIndex: 0,
            pageSize: pagination.pageSize,
          },
        };
      });
      refetchProfiles();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages, pagination.pageIndex, firstLoad, refetchProfiles]);

  useEffect(() => {
    setIsProfileListRefetchNeeded(
      profiles?.items?.some(
        (profile) =>
          profile.status !== BizProfileModels.BizProfileListStatusEnum.processed &&
          profile.status !== BizProfileModels.BizProfileListStatusEnum.processing_failed,
      ) || false,
    );
  }, [profiles?.items]);

  const openEditUploadsModal = (row?: BizProfileModels.BizProfileListDto) => {
    if (!row) {
      return;
    }
    setSingleProfileEdit(row);
    setIsEditUploadsModalOpen(true);
  };

  const { columns } = useUploadsPageColumns({
    setIsDeleteProfileModalOpen,
    openEditUploadsModal,
  });

  const { checkForValidNonSecurePdfFiles } = useFileValidationCheck();

  const getRowClassName = useCallback((row: BizProfileModels.BizProfileListDto) => {
    const isRowProcessed = row.status === BizProfileModels.BizProfileListStatusEnum.processed;
    return !row.isRead && isRowProcessed ? "relative !bg-[#242424] hover:!bg-opacity-25" : "relative";
  }, []);

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const validFiles = await checkForValidNonSecurePdfFiles(Array.from(e.target.files), { maxSizeMB: 20 });

      if (validFiles?.length) {
        setSelectedFiles(validFiles);
        setIsModalOpen(true);
      }

      // oxlint-disable-next-line no-param-reassign
      e.target.value = "";
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const onEditTagsClick = () => {
    setIsEditUploadsModalOpen(true);
  };

  const onDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const onTagSelectionChange = (tags: Map<string, string[]>) => {
    setSelectedTags(tags);
    setRowSelection({});
    setSelectAllResumes(false);
    updateTableStateLocalStorage2((curr) => {
      return {
        ...curr,
        selectedTags: Object.fromEntries(tags),
        pagination: {
          pageIndex: 0,
          pageSize: pagination.pageSize,
        },
      };
    });
  };

  const onMarkAsReadClick = () => {
    const selectedRows = TableUtils.getSelectedRows(rowSelection, profiles?.items);
    // Filter out processing_failed rows - only allow processed rows for Mark as Read
    const processedRowsOnly = selectedRows?.filter(
      (item) => item.status === BizProfileModels.BizProfileListStatusEnum.processed,
    );

    markAsRead({
      data: { ids: processedRowsOnly?.map((item) => item.id) || [] },
    });
  };

  const areDefaultFiltersApplied = useMemo(() => {
    // Get all keys from the Map
    const mapKeys = Array.from(selectedTags.keys());

    // Check if every field in the Map has length 0
    const allFieldsEmpty = mapKeys.every((key) => {
      const values = selectedTags.get(key);
      return !values || values.length === 0;
    });

    return mapKeys.length === 0 || allFieldsEmpty;
  }, [selectedTags]);

  const onSelectAllRowsClick = () => {
    setRowSelection(
      profiles?.items?.reduce((acc, _item, index) => {
        acc[index] = true; // oxlint-disable-line no-param-reassign
        return acc;
      }, {} as RowSelectionState) || {},
    );
  };

  const onClearSelectionClick = () => {
    setRowSelection({});
    setSelectAllResumes(false);
  };

  return (
    <div className="relative flex h-full flex-1 flex-col">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        className="hidden"
        accept=".pdf"
        multiple
      />
      <div className="flex items-center justify-between px-10 pt-5">
        <TextInput
          icon={SearchIcon}
          label=""
          hideLabels
          placeholder={t("uploads.userUploads.search")}
          variant="search"
          className="max-w-md"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            updateTableStateLocalStorage2((curr) => {
              return {
                ...curr,
                searchValue: e.target.value,
                pagination: {
                  pageIndex: 0,
                  pageSize: pagination.pageSize,
                },
              };
            });
          }}
        />
        <Button
          icon={PlusIcon}
          variant="secondary"
          labelSize="label-4"
          width="auto"
          iconClassName="w-3 h-3"
          className="!gap-2-5 !px-3 !py-3-5 border-gradients-gradient-500"
          labelClassName="!text-label-4 !font-body-default"
          iconPosition="right"
          onPress={handleUploadClick}
        >
          {t("uploads.userUploads.upload-resumes")}
        </Button>
      </div>
      <div className="flex h-full flex-1 flex-col px-10 pt-3">
        <div className="flex items-start justify-between pb-3">
          <UploadsPagePillFilters
            selectedTags={selectedTags}
            setSelectedTags={onTagSelectionChange}
          />
          <div className="flex min-w-36 items-center justify-end">
            <NewPagination
              currentPage={pagination.pageIndex}
              pageSize={pagination.pageSize}
              totalItems={profiles?.totalItems || 0}
              onPageChange={(page) => {
                const newPagination = { ...pagination, pageIndex: page };
                setPagination(newPagination);
                setRowSelection({});
                setSelectAllResumes(false);
                updateTableStateLocalStorage2((curr) => {
                  return {
                    ...curr,
                    pagination: newPagination,
                  };
                });
              }}
            />
          </div>
        </div>

        <Table
          data={profiles?.items || []}
          columns={columns}
          sorting={sorting}
          onSortingChange={(updaterOrValue) => {
            const newSorting = typeof updaterOrValue === "function" ? updaterOrValue(sorting) : updaterOrValue;
            setSorting(newSorting);
            updateTableStateLocalStorage2((curr) => {
              return {
                ...curr,
                sorting: newSorting,
                pagination,
              };
            });
          }}
          isLoading={isLoadingProfiles}
          rowSelection={rowSelection}
          onRowSelectionChange={(updaterOrValue) => {
            const newRowSelection =
              typeof updaterOrValue === "function" ? updaterOrValue(rowSelection) : updaterOrValue;
            setRowSelection(newRowSelection);
            setSelectAllResumes(false);
          }}
          className="!max-w-[100%]"
          rowClassname={getRowClassName}
        />
        {Object.keys(rowSelection).length > 0 && (
          <div className="-ml-12 sticky right-0 bottom-0 left-0 flex w-[calc(100%+98px)] items-center justify-between border-elevation-outline-1 border-t bg-elevation-background p-5">
            <div className="flex flex-row items-center gap-3">
              <Typography
                size="label-4"
                variant="default"
                className="flex-shrink-0 whitespace-nowrap text-text-default-primary"
              >
                {selectAllResumes ? profiles?.totalItems || 0 : Object.keys(rowSelection).length} Selected:
              </Typography>
              {Object.keys(rowSelection).length === (profiles?.items?.length || 0) &&
                !selectAllResumes &&
                profiles?.totalItems &&
                profiles?.totalItems > 10 && (
                  <TextButton
                    size="label-4"
                    variant="default"
                    onPress={() => setSelectAllResumes(true)}
                    className="underline"
                  >
                    {t("uploads.userUploads.selectAllResumes", { count: profiles?.totalItems || 0 })}
                  </TextButton>
                )}
              {!selectAllResumes && Object.keys(rowSelection).length < (profiles?.items?.length || 0) && (
                <Button
                  labelSize="label-4"
                  variant="tertiary"
                  className="!p-3 !font-secondary"
                  onPress={onSelectAllRowsClick}
                >
                  Select All
                </Button>
              )}
              <Button
                labelSize="label-4"
                variant="outlined"
                className="!border-elevation-outline-1 !p-3"
                onPress={onClearSelectionClick}
              >
                Clear Selection
              </Button>
            </div>

            <div className="flex items-center gap-2">
              {!selectAllResumes && (
                <>
                  <IconButton
                    icon={MarkAsReadIcon}
                    label="Mark as Read"
                    variant="outlined"
                    className="!border-elevation-outline-1 !px-2 !py-2"
                    iconClassName="w-6 h-6"
                    onPress={onMarkAsReadClick}
                    showTooltip
                  />
                  <IconButton
                    icon={Edit2Icon}
                    label="Edit"
                    variant="outlined"
                    className="!border-elevation-outline-1 !px-2 !py-2"
                    iconClassName="w-6 h-6"
                    onPress={onEditTagsClick}
                    showTooltip
                  />
                </>
              )}
              <IconButton
                icon={DeleteIcon}
                label="Delete"
                variant="outlined"
                className="!border-elevation-outline-1 !px-2 !py-2"
                iconClassName="w-6 h-6 text-info-error-2"
                onPress={onDeleteClick}
                showTooltip
              />
            </div>
          </div>
        )}

        {!profiles?.items?.length && !isLoadingProfiles && areDefaultFiltersApplied && (
          <UploadsPageDropZone
            onFilesDropped={(files) => {
              if (!files?.length) {
                return;
              }
              setSelectedFiles(files);
              setIsModalOpen(true);
            }}
            onUploadClick={handleUploadClick}
          />
        )}

        <UploadsPageDeleteProfilesModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setRowSelection({});
            setIsDeleteModalOpen(false);
            setSelectAllResumes(false);
          }}
          selectedProfiles={
            selectAllResumes ? profiles?.items || [] : TableUtils.getSelectedRows(rowSelection, profiles?.items)
          }
          selectAllResumes={selectAllResumes}
          setSelectAllResumes={setSelectAllResumes}
          totalProfiles={profiles?.totalItems || 0}
          pagination={{
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            order: sorting?.[0] ? `${sorting[0].desc ? "-" : "+"}${sorting[0].id}` : undefined,
            filterExperienceYearsMax:
              selectedTags.get("experience")?.map((exp) => experienceFilter.find((e) => e.id === exp))?.[0]?.max ||
              undefined,
            filterExperienceYearsMin:
              selectedTags.get("experience")?.map((exp) => experienceFilter.find((e) => e.id === exp))?.[0]?.min ||
              undefined,
            filterRating:
              (selectedTags.get("rating") as unknown as number[])?.map((rating) =>
                rating.toString() === "Unrated" ? -1 : rating,
              ) || [],
            filterMsaLocation: selectedTags.get("location")?.[0] || "",
            filterTagName: selectedTags.get("tag") || [],
            filterJobTitle: selectedTags.get("jobTitle")?.[0] || "",
            filterSearch: debouncedSearchTerm,
          }}
        />
        {isEditUploadsModalOpen && (
          <UploadsPageEditProfilesModal
            isOpen={isEditUploadsModalOpen}
            onClose={() => {
              setIsEditUploadsModalOpen(false);
              setSingleProfileEdit(undefined);
            }}
            selectedProfiles={TableUtils.getSelectedRows(rowSelection, profiles?.items)}
            singleProfileEdit={singleProfileEdit}
            onBulkUpdateSuccess={() => {
              setRowSelection({});
              setSelectAllResumes(false);
            }}
          />
        )}

        <UploadsPageFilesUploadModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedFiles([]);
          }}
          selectedFiles={selectedFiles}
        />
        <ActionModal
          isOpen={!!isDeleteProfileModalOpen}
          onClose={() => setIsDeleteProfileModalOpen(undefined)}
          heading={`${t("uploads.profile.delete-profile.heading")} ${isDeleteProfileModalOpen?.name}`}
          description={t("uploads.profile.delete-profile.description")}
          primaryAction={{
            label: t("uploads.profiles-actions-modal.delete-button"),
            onPress: () => {
              if (isDeleteProfileModalOpen) {
                deleteBizProfile({ id: isDeleteProfileModalOpen.id });
              }
            },
          }}
          secondaryAction={{
            label: t("uploads.profiles-actions-modal.cancel-button"),
            onPress: () => setIsDeleteProfileModalOpen(undefined),
          }}
        />
      </div>
    </div>
  );
};

export default UploadsPage;
